import {
  initialize as initializeLD,
  type LDClient,
  type LDContext,
} from 'launchdarkly-js-client-sdk'

import { type User } from '@webapp/models'
import { readFFOverrides } from './devFFOverrides'

// Flag defaults live in ./constants so they can be consumed without loading
// the LaunchDarkly SDK; re-exported here to preserve this module's public API.
export { FEATURE_FLAGS_CONFIG, FEATURE_FLAG_DEFAULTS } from './constants'

interface FeatureFlagServiceConfig {
  clientId: string
  user: User
  domain: string
  basePath?: string | null
}

/**
 * When the backend reports a dev-server base path, point every SDK endpoint at
 * it (same origin, so no CORS). Otherwise the SDK uses LaunchDarkly's defaults.
 */
export const getLDServiceEndpoints = (basePath?: string | null) => {
  if (!basePath) return {}
  const url = `${window.location.origin}${basePath}`
  return { baseUrl: url, streamUrl: url, eventsUrl: url }
}

export class FeatureFlagService {
  private static instance: FeatureFlagService | null = null
  private readonly client: LDClient
  private readonly clientId: string
  private readonly options: LDContext
  initialized: boolean = false

  private constructor(config: FeatureFlagServiceConfig) {
    const { clientId, user, domain, basePath } = config

    this.clientId = clientId

    this.options = {
      key: 'aa0ceb',
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      custom: {
        domain,
      },
    }

    this.client = initializeLD(this.clientId, this.options, {
      sendEventsOnlyForVariation: true,
      streaming: true,
      ...getLDServiceEndpoints(basePath),
    })
  }

  public static async initialize(
    config: FeatureFlagServiceConfig
  ): Promise<FeatureFlagService> {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService(config)
      await FeatureFlagService.instance.initializeClient()
    }
    return FeatureFlagService.instance
  }

  public static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      throw new Error('FeatureFlagService has not been initialized')
    }
    return FeatureFlagService.instance
  }

  private initializeClient(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client.on('ready', () => {
        this.initialized = true
        resolve()
      })
      this.client.on('failed', (error) => {
        this.initialized = false
        reject(new Error(`LD Client failed to initialize: ${error}`))
      })
    })
  }

  public getFlag(flagKey: string) {
    if (!this.initialized) {
      throw new Error('getFlags method: FeatureFlagService is not initialized')
    }

    // Dev-only escape hatch (empty outside IS_DEV): consumers read flags
    // through this service, not the Redux store, so overrides from the
    // ProcurifyDevTools modal / window.__procurifyDev must be applied here.
    const overrides = readFFOverrides()
    if (flagKey in overrides) {
      return overrides[flagKey]
    }

    return this.client.variation(flagKey)
  }

  public getAllFlags() {
    if (!this.initialized) {
      throw new Error(
        'getAllFlags method: FeatureFlagService is not initialized'
      )
    }
    return this.client.allFlags()
  }
}
